import { ANALYTICS_CONFIG } from './config';


/**
 * Fire custom event
 */
const fireEvent = (eventName: string, eventData?: Record<string, any>): void => {
  try {
    if (globalThis.window !== undefined && globalThis.digitalData) {
      if (eventData) {
        globalThis.digitalData.event = {
          ...(globalThis.digitalData.event ),
          ...eventData,
        } ;
      }
    }

    // Check if _satellite is available
    if (typeof window !== 'undefined' && window._satellite) {
      console.log(`📌 Tracking Event: ${eventName}`, eventData);
      window._satellite.track(eventName);
    } else {
      // Queue the event if _satellite is not ready yet
      console.log(`📝 Event Queued (Adobe _satellite not ready): ${eventName}`, eventData);
      queueEventCall(eventName);
    }
  } catch (error) {
    console.error(`❌ Error tracking event ${eventName}:`, error);
  }
};

/**
 * Queue event calls until Adobe _satellite is ready
 */
const eventQueue: string[] = [];

const queueEventCall = (eventName: string): void => {
  eventQueue.push(eventName);
  
  // Retry every 500ms for up to 30 seconds
  const startTime = Date.now();
  const checkInterval = setInterval(() => {
    if (typeof window !== 'undefined' && window._satellite) {
      // _satellite is now available, flush the queue
      console.log(`✅ Adobe _satellite loaded! Flushing ${eventQueue.length} queued events...`);
      while (eventQueue.length > 0) {
        const queuedEvent = eventQueue.shift();
        if (queuedEvent) {
          try {
            window._satellite.track(queuedEvent);
            console.log(`✅ Flushed queued event: ${queuedEvent}`);
          } catch (e) {
            console.error(`❌ Error flushing queued event:`, e);
          }
        }
      }
      clearInterval(checkInterval);
    } else if (Date.now() - startTime > 30000) {
      // Stop trying after 30 seconds
      console.warn(`⚠️ Adobe _satellite not loaded after 30s. Clearing event queue.`);
      eventQueue.length = 0;
      clearInterval(checkInterval);
    }
  }, 500);
};


/**
 * Track form submission
 */
export const trackFormSubmit = (
  app: 'citizenship' | 'tuition',
  step?: number,
  data?: Record<string, any>
): void => {
  fireEvent(ANALYTICS_CONFIG.EVENTS.FORM_SUBMIT, {
    action: 'Form Submission',
    application: app,
    step,
    timestamp: new Date().toISOString(),
    ...data,
  });
};

/**
 * Track form save
 */
export const trackFormSave = (
  app: 'citizenship' | 'tuition',
  step?: number,
  data?: Record<string, any>
): void => {
  fireEvent(ANALYTICS_CONFIG.EVENTS.FORM_SAVE, {
    action: 'Form Save',
    application: app,
    step,
    timestamp: new Date().toISOString(),
    ...data,
  });
};

/**
 * Track form errors
 */
export const trackFormError = (
  app: 'citizenship' | 'tuition',
  errorFields: string[],
  step?: number
): void => {
  fireEvent(ANALYTICS_CONFIG.EVENTS.FORM_ERROR, {
    action: 'Form Validation Error',
    application: app,
    step,
    errorFields,
    errorCount: errorFields.length,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track button clicks
 */
export const trackButtonClick = (
  buttonName: string,
  location?: string,
  additionalData?: Record<string, any>
): void => {
  fireEvent(ANALYTICS_CONFIG.EVENTS.BUTTON_CLICK, {
    buttonClick: {
      buttonName,
      buttonLocation: location || 'unknown',
      clickType: 'primary',
    },
    ...additionalData,
  });
};

/**
 * Track search actions
 */
export const trackSearch = (
  searchType: string,
  searchTerm: string,
  resultsFound: number,
  additionalData?: Record<string, any>
): void => {
  fireEvent(ANALYTICS_CONFIG.EVENTS.SEARCH, {
    search: {
      searchType,
      searchTerm,
      searchResults: resultsFound,
      searchResultPosition: '',
    },
    ...additionalData,
  });
};

/**
 * Track manager approval
 */
export const trackApproval = (
  app: 'citizenship' | 'tuition',
  applicationId?: string,
  notes?: string
): void => {
  fireEvent(ANALYTICS_CONFIG.EVENTS.APPROVAL, {
    action: 'Application Approved',
    application: app,
    applicationId,
    notes,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track manager denial
 */
export const trackDenial = (
  app: 'citizenship' | 'tuition',
  applicationId?: string,
  reason?: string
): void => {
  fireEvent(ANALYTICS_CONFIG.EVENTS.DENIAL, {
    action: 'Application Denied',
    application: app,
    applicationId,
    reason,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track document upload
 */
export const trackDocumentUpload = (
  app: 'citizenship' | 'tuition',
  documentType: string,
  success: boolean,
  error?: string
): void => {
  fireEvent(ANALYTICS_CONFIG.EVENTS.DOCUMENT_UPLOAD, {
    action: 'Document Upload',
    application: app,
    documentType,
    success,
    error,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track navigation
 */
export const trackNavigation = (
  from: string,
  to: string,
  direction: 'next' | 'back'
): void => {
  fireEvent('navigation', {
    action: `Navigate ${direction}`,
    fromPage: from,
    toPage: to,
    direction,
    timestamp: new Date().toISOString(),
  });
};